import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAgent: boolean;
  isBrokerAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authInitializing, setAuthInitializing] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const [isBrokerAdmin, setIsBrokerAdmin] = useState(false);

  // Combined loading state
  const isLoading = authInitializing || rolesLoading;

  // Effect 1: Set up auth listener and initialize session
  useEffect(() => {
    // Set up auth state listener FIRST - MUST be synchronous callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Synchronous state updates only
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Reset roles when signed out
        if (!newSession?.user) {
          setIsAgent(false);
          setIsBrokerAdmin(false);
        }
      }
    );

    // THEN check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
      } catch (error) {
        console.error("[Auth] Error initializing:", error);
      } finally {
        setAuthInitializing(false);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Effect 2: Fetch roles when user changes (separate from auth callback)
  useEffect(() => {
    if (!user?.id) {
      // No user, reset roles
      setIsAgent(false);
      setIsBrokerAdmin(false);
      return;
    }

    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const { data: roles, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (error) {
          console.error("[Auth] Error fetching roles:", error);
          return;
        }

        const hasAgent = roles?.some((r) => r.role === "agent" || r.role === "broker_admin") ?? false;
        const hasBrokerAdmin = roles?.some((r) => r.role === "broker_admin") ?? false;
        
        setIsAgent(hasAgent);
        setIsBrokerAdmin(hasBrokerAdmin);
      } catch (error) {
        console.error("[Auth] Error in role fetch:", error);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{ session, user, isLoading, isAgent, isBrokerAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
